/**
 * AmbassadorSaveTip — One-time post-signup nudge to bookmark / pin / save
 * password. Platform-aware copy so the steps match what the ambassador
 * actually sees on their device.
 *
 * Hides itself when:
 *   - the user has previously dismissed it (localStorage)
 *   - the page is already running as an installed PWA (display-mode: standalone)
 *
 * Otherwise shows a soft gold panel with the right steps for iOS, Android,
 * or Desktop.
 */

import { useEffect, useState } from 'react';
import { POS_GOLD } from '../../config/posRoomStatus';

const DISMISS_KEY = 'ambassador-save-tip-dismissed-v1';

type Platform = 'ios' | 'android' | 'desktop' | 'unknown';

function detectPlatform(): Platform {
  if (typeof navigator === 'undefined') return 'unknown';
  const ua = navigator.userAgent || '';
  if (/iPad|iPhone|iPod/.test(ua)) return 'ios';
  if (/Android/.test(ua)) return 'android';
  return 'desktop';
}

function isStandalone(): boolean {
  if (typeof window === 'undefined') return false;
  if (window.matchMedia?.('(display-mode: standalone)').matches) return true;
  // iOS Safari uses a different signal
  if ((window.navigator as Navigator & { standalone?: boolean }).standalone === true) return true;
  return false;
}

function AmbassadorSaveTip() {
  const [show, setShow] = useState(false);
  const [platform, setPlatform] = useState<Platform>('unknown');

  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (isStandalone()) return;
    try {
      if (localStorage.getItem(DISMISS_KEY) === '1') return;
    } catch {
      // localStorage may be blocked — show the tip anyway
    }
    setPlatform(detectPlatform());
    setShow(true);
  }, []);

  function dismiss() {
    try {
      localStorage.setItem(DISMISS_KEY, '1');
    } catch {
      // ignore
    }
    setShow(false);
  }

  if (!show) return null;

  let heading = 'Save this dashboard for next time';
  let steps: string[] = [];

  if (platform === 'ios') {
    heading = 'Add this to your iPhone home screen';
    steps = [
      'Tap the Share button at the bottom of your Safari screen (a square with an arrow pointing up).',
      'Scroll down and tap "Add to Home Screen".',
      'Tap "Add" in the top-right corner.',
      'You can now tap the gold Peace of Sleep icon on your home screen to open the dashboard like an app.',
    ];
  } else if (platform === 'android') {
    heading = 'Add this to your Android home screen';
    steps = [
      'Tap the menu button (three dots, top right of your browser).',
      'Tap "Add to Home screen" or "Install app".',
      'Tap "Add" or "Install" to confirm.',
      'You can now tap the icon on your home screen to open the dashboard like an app.',
    ];
  } else {
    heading = 'Bookmark this dashboard for next time';
    steps = [
      'Press ⌘ + D on a Mac or Ctrl + D on a Windows PC to bookmark this page.',
      'Look for an install icon in your browser address bar (often a small + or screen-with-arrow symbol) to pin it as an app.',
      'Let your browser save your password when it offers — you will not need to type it again.',
    ];
  }

  return (
    <div
      style={{
        background: 'var(--color-white)',
        border: `1px solid ${POS_GOLD}`,
        borderLeft: `4px solid ${POS_GOLD}`,
        borderRadius: 'var(--radius)',
        padding: '18px 22px',
        marginBottom: '24px',
        boxShadow: '0 1px 4px rgba(0,0,0,0.04)',
      }}
    >
      <div className="flex justify-between items-start gap-lg">
        <div className="flex-1">
          <p style={{ margin: '0 0 10px', fontWeight: 600, fontSize: 'var(--text-lg)', color: 'var(--color-sidebar)' }}>
            {heading}
          </p>
          <p style={{ margin: '0 0 12px', fontSize: 'var(--text-md)', color: 'var(--color-text-light)' }}>
            Save yourself the hassle of typing the address every time. Two minutes now, lifetime convenience.
          </p>
          <ol style={{ margin: 0, paddingLeft: '20px', fontSize: 'var(--text-md)', color: 'var(--color-sidebar)', lineHeight: 1.55 }}>
            {steps.map((step, i) => (
              <li key={i} className="mb-xs">{step}</li>
            ))}
          </ol>
          {platform !== 'desktop' && (
            <p style={{ margin: '12px 0 0', fontSize: 'var(--text-md)', color: 'var(--color-text-light)' }}>
              When your browser offers to save your password, tap <strong>Save</strong>. You will not need to type it again next time.
            </p>
          )}
        </div>
        <button
          type="button"
          onClick={dismiss}
          aria-label="Dismiss tip"
          style={{
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            fontSize: 'var(--text-2xl)',
            color: 'var(--color-text-light)',
            padding: '4px 8px',
            lineHeight: 1,
          }}
        >
          ×
        </button>
      </div>
    </div>
  );
}

export default AmbassadorSaveTip;
